import React from 'react';
import { Mail, TrendingUp, Trophy, Bell, Download } from 'lucide-react';
import { KpiCard, BarViz, LoadingSkeleton } from './_shared';

function exportCsv(data) {
  const rows = [['Magnet', 'Leads', 'Latest lead']];
  (data.magnets || []).forEach((m) => rows.push([m.magnet, m.count, m.latest || '']));
  rows.push([]);
  rows.push(['Source', 'Leads']);
  (data.sources || []).forEach((s) => rows.push([s.source, s.count]));
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `funnel-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function FunnelTab({ data }) {
  if (!data) return <LoadingSkeleton />;
  const magnets = data.magnets || [];
  const sources = data.sources || [];
  const trend = data.trend || [];
  const race = data.race_alerts || [];
  const maxMagnet = Math.max(...magnets.map((m) => m.count), 1);
  const maxTrend = Math.max(...trend.map((t) => t.count), 1);
  const top = magnets[0];

  return (
    <div className="space-y-6" data-testid="funnel-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-gray-900">Lead-Magnet Funnel</h2>
        <button
          onClick={() => exportCsv(data)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          data-testid="funnel-export-csv"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Mail} label="Total Leads" value={data.total_leads ?? 0} color="#14b8a6" />
        <KpiCard icon={TrendingUp} label="Last 30 Days" value={data.leads_30d ?? 0} color="#8b5cf6" />
        <KpiCard icon={Trophy} label="Top Magnet" value={top ? top.magnet : '—'} subtext={top ? `${top.count} leads` : ''} color="#f59e0b" />
        <KpiCard icon={Bell} label="Race-Alert Subs" value={data.race_alert_total ?? 0} color="#ef4444" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="funnel-magnet-bars">
          <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Leads by Magnet</h3>
          {magnets.length === 0 ? (
            <p className="text-xs text-gray-400">No leads captured yet.</p>
          ) : (
            <BarViz
              items={magnets.slice(0, 12).map((m) => ({ label: m.magnet, value: `${m.count}`, raw: m.count }))}
              maxVal={maxMagnet}
              color="#14b8a6"
            />
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="funnel-sources">
          <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Top Converting Sources</h3>
          {sources.length === 0 ? (
            <p className="text-xs text-gray-400">No source data yet.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {sources.slice(0, 12).map((s) => (
                  <tr key={s.source} className="border-b border-gray-50 last:border-0">
                    <td className="py-2 text-gray-700 truncate max-w-[240px]">{s.source}</td>
                    <td className="py-2 text-right text-gray-500 font-medium">{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="funnel-trend">
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Leads — Last 30 Days</h3>
        {trend.length === 0 ? (
          <p className="text-xs text-gray-400">No leads in the last 30 days.</p>
        ) : (
          <div>
            <div className="flex items-end gap-1 h-28">
              {trend.map((t) => (
                <div key={t.date} className="flex-1 h-full flex flex-col justify-end items-center group" title={`${t.date}: ${t.count}`}>
                  <div className="w-full bg-teal-400 rounded-t group-hover:bg-teal-500 transition-colors" style={{ height: `${Math.max((t.count / maxTrend) * 100, 4)}%` }} />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-gray-400">
              <span>{trend[0].date}</span>
              <span>{trend[trend.length - 1].date}</span>
            </div>
          </div>
        )}
      </div>

      {race.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="funnel-race-alerts">
          <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Race-Alert Subscribers by City</h3>
          <BarViz
            items={race.map((r) => ({ label: r.city, value: `${r.count}`, raw: r.count }))}
            maxVal={Math.max(...race.map((r) => r.count), 1)}
            color="#ef4444"
          />
        </div>
      )}
    </div>
  );
}
